import React, { useState, useCallback } from 'react';
import { Badge, Dropdown, Button, Empty } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useUnifiedWebSocket } from '../../hooks/useUnifiedWebSocket';
import { WebSocketChannel, WebSocketMessageType } from '../../types/websocket';
import type { NotificationPayload, WebSocketMessage } from '../../types/websocket';
import styles from './NotificationsBell.module.css';

const typeColor: Record<NotificationPayload['type'], string> = {
  info: '#ff5f6d',
  success: '#52c41a',
  warning: '#faad14',
  error: '#f5222d',
};

/** Header bell wired to the real backend `notifications` WebSocket channel. */
const NotificationsBell: React.FC = () => {
  const [items, setItems] = useState<NotificationPayload[]>([]);
  const [unread, setUnread] = useState(0);

  const onMessage = useCallback((m: WebSocketMessage) => {
    if (m.type === WebSocketMessageType.NOTIFICATION) {
      const payload = m.payload as NotificationPayload;
      setItems((prev) => [payload, ...prev].slice(0, 30));
      setUnread((u) => u + 1);
    }
  }, []);

  useUnifiedWebSocket({ channels: [WebSocketChannel.NOTIFICATIONS], onMessage });

  const dropdownRender = () => (
    <div className={styles.panel}>
      <div className={styles.head}>Сповіщення</div>
      {items.length === 0 ? (
        <div className={styles.emptyWrap}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Поки немає сповіщень" />
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((n) => (
            <div key={n.id} className={styles.item}>
              <span className={styles.dot} style={{ background: typeColor[n.type] || '#ff5f6d' }} />
              <div className={styles.body}>
                <div className={styles.title}>{n.title}</div>
                <div className={styles.msg}>{n.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      dropdownRender={dropdownRender}
      onOpenChange={(o) => {
        // Clear on close, not open: while the panel is up the items still
        // read as new; closing it is the "seen" signal.
        if (!o) setUnread(0);
      }}
    >
      <Badge count={unread} size="small" offset={[-2, 4]}>
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined />}
          aria-label="Сповіщення"
          className={styles.bell}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationsBell;
